import { prefetchErpNavigation } from './prefetchErpNavigation';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    ImagePlus, Layers, Video, Warehouse, Settings, Sparkles,
    ClipboardList, Package, User, ChevronsLeft, ChevronsRight, LogOut,
} from 'lucide-react';
import { ErpRole } from './erpAccess';

// Nav layout + per-role visibility:
//   admin    → 直播·采购·库存·货盘(all suppliers, read-only)·设置·AI排品·图片工具
//   supplier → 商店 (own catalog) · 订单 · 我的 (store profile)
// The /erp/products tab is labeled 货盘 for admin (browse all suppliers' pool) but 商店
// for a supplier (their own shop). 库存 is the former 商品 page (/erp/inventory), relabeled.
export const ERP_NAV_ITEMS: NavItem[] = [
    { label: '直播', Icon: Video,        path: '/erp/live',      roles: ['admin'],              isActive: (p: string) => p.startsWith('/erp/live') },
    { label: '采购', Icon: Layers,       path: '/erp/purchase',  roles: ['admin'],              isActive: (p: string) => p === '/erp/purchase' || p.startsWith('/erp/purchase/') },
    { label: '订单', Icon: ClipboardList, path: '/erp/orders',    roles: ['supplier'],           isActive: (p: string) => p.startsWith('/erp/orders') },
    { label: '库存', Icon: Warehouse,    path: '/erp/inventory', roles: ['admin'],              isActive: (p: string) => p.startsWith('/erp/inventory') },
    { label: '货盘', Icon: Package,      path: '/erp/products',  roles: ['admin', 'supplier'],  isActive: (p: string) => p.startsWith('/erp/products') },
    { label: '我的', Icon: User,         path: '/erp/profile',   roles: ['supplier'],           isActive: (p: string) => p.startsWith('/erp/profile') },
    { label: '设置', Icon: Settings,     path: '/erp/settings',  roles: ['admin'],              isActive: (p: string) => p.startsWith('/erp/settings') },
    { label: 'AI 排品', Icon: Sparkles,  path: '/erp/ai-planning', roles: ['admin'],            isActive: (p: string) => p.startsWith('/erp/ai-planning') },
    { label: '图片工具', Icon: ImagePlus, path: '/erp/image-tools', roles: ['admin'],            isActive: (p: string) => p.startsWith('/erp/image-tools') },
];

interface NavItem {
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
    path: string | null;
    roles: ErpRole[];
    isActive: (pathname: string) => boolean;
}

// Supplier tab order: 商店 → 订单 → 我的.
const SUPPLIER_NAV_ORDER = ['/erp/products', '/erp/orders', '/erp/profile'];

export function navItemsForRole(role: ErpRole): NavItem[] {
    let items = ERP_NAV_ITEMS.filter(item => item.roles.includes(role));
    if (role === 'supplier') {
        items = items
            .map(item => item.path === '/erp/products' ? { ...item, label: '商店' } : item)
            .sort((a, b) => SUPPLIER_NAV_ORDER.indexOf(a.path ?? '') - SUPPLIER_NAV_ORDER.indexOf(b.path ?? ''));
    }
    return items;
}

interface ErpAdminSidebarProps {
    onLock: () => void;
    role: ErpRole;
}

export const ErpAdminSidebar: React.FC<ErpAdminSidebarProps> = ({ onLock, role }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(() =>
        localStorage.getItem('sseeaa_erp_sidebar_collapsed') === 'true'
    );
    const navItems = navItemsForRole(role);

    const toggle = () => {
        const next = !isCollapsed;
        setIsCollapsed(next);
        localStorage.setItem('sseeaa_erp_sidebar_collapsed', String(next));
    };

    return (
        <aside
            className={`flex flex-col bg-white border-r border-slate-100 flex-shrink-0 transition-all duration-300 ${
                isCollapsed ? 'w-14' : 'w-52'
            }`}
            style={{ height: '100%' }}
        >
            {/* Header */}
            <div
                className={`flex items-center h-[60px] flex-shrink-0 ${
                    isCollapsed ? 'justify-center px-2' : 'justify-between pl-5 pr-2'
                }`}
            >
                {!isCollapsed && (
                    <img
                        src="/images/Logo_Color.svg"
                        alt="SSEEAA"
                        className="h-5 w-auto cursor-pointer"
                        onClick={() => navigate('/')}
                    />
                )}
                <button
                    onClick={toggle}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-all duration-200"
                    title={isCollapsed ? '展开菜单' : '收起菜单'}
                >
                    {isCollapsed
                        ? <ChevronsRight className="w-4 h-4" />
                        : <ChevronsLeft className="w-4 h-4" />
                    }
                </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto py-2 px-2">
                {(navItems as NavItem[]).map(({ label, Icon, path, isActive: matchFn }) => {
                    const isActive = matchFn(location.pathname);
                    return (
                        <button
                            key={label}
                            title={isCollapsed ? label : undefined}
                            onClick={() => { if (path) navigate(path); }}
                            onPointerEnter={() => prefetchErpNavigation(path)}
                            onFocus={() => prefetchErpNavigation(path)}
                            onPointerDown={() => prefetchErpNavigation(path)}
                            className={`w-full flex items-center h-10 rounded-xl transition-all duration-200 overflow-hidden mb-0.5 cursor-pointer ${
                                isActive
                                    ? 'bg-btn-primary/10 text-btn-primary font-medium'
                                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                            }`}
                        >
                            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                                <Icon className="w-4 h-4" />
                            </div>
                            <div
                                className={`overflow-hidden transition-all duration-300 ${
                                    isCollapsed ? 'w-0 opacity-0' : 'flex-1 opacity-100'
                                }`}
                            >
                                <span className="text-sm whitespace-nowrap pr-4">{label}</span>
                            </div>
                        </button>
                    );
                })}
            </nav>

            {/* Lock / exit */}
            <div className="flex-shrink-0 px-2 pb-4">
                <button
                    onClick={onLock}
                    title={isCollapsed ? '退出管理' : undefined}
                    className="w-full flex items-center h-10 rounded-xl text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-all duration-200"
                >
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <LogOut className="w-4 h-4" />
                    </div>
                    <div
                        className={`overflow-hidden transition-all duration-300 ${
                            isCollapsed ? 'w-0 opacity-0' : 'flex-1 opacity-100'
                        }`}
                    >
                        <span className="text-sm whitespace-nowrap">退出管理</span>
                    </div>
                </button>
            </div>
        </aside>
    );
};
